import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';
import { PrismComboSelectInput } from '@mylighthouse/prism-ember';
import type { Option } from '@mylighthouse/prism-ember/types/components/prism/select-input';
import { t } from 'ember-intl';
import {
  SMART_INSIGHT_PRIORITIES,
  SMART_INSIGHT_PRIORITY_ICONS,
  SMART_INSIGHT_PRIORITY_TRANSLATIONS,
  type SmartInsightPriority,
} from 'lumen/models/smart-insights/smart-insight-day';

type DropdownPriorityFilterOption = Option & {
  id: SmartInsightPriority;
};

export interface GeneralSmartInsightsDropdownPriorityFilterSignature {
  Args: {
    isSmall?: boolean;
    selectedPriorities: SmartInsightPriority[];
    setSelectedPriorities: (priorities: SmartInsightPriority[]) => void;
  };
}

export default class GeneralSmartInsightsDropdownPriorityFilter extends Component<GeneralSmartInsightsDropdownPriorityFilterSignature> {
  @service declare intl: Services['intl'];
  @service declare usageAnalytics: Services['usage-analytics'];

  // Preserve reference for isEqual check in PowerSelect code
  @cached
  get options(): DropdownPriorityFilterOption[] {
    return Object.values(SMART_INSIGHT_PRIORITIES).map((priority) => ({
      id: priority,
      label: this.intl.t(SMART_INSIGHT_PRIORITY_TRANSLATIONS[priority]),
      icon: SMART_INSIGHT_PRIORITY_ICONS[priority],
    }));
  }

  get selectedOptions(): DropdownPriorityFilterOption[] {
    return this.options.filter((option) =>
      this.args.selectedPriorities.includes(option.id),
    );
  }

  @action
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  buildLabelWithCount(count: number) {
    return this.intl.t(
      'market.smart_insights.filter.priority.count_priorities',
      { count },
    );
  }

  @action onChange(options: DropdownPriorityFilterOption[]): void {
    const priorityIds = options.map((option) => option.id);

    this.args.setSelectedPriorities(priorityIds);

    this.usageAnalytics.track('Smart Insights Filtered', {
      eventTrigger: 'smart-insights-priority-filter',
      product: 'smart-insights-multi-property',
      type: 'priority',
      values: priorityIds,
    });
  }

  <template>
    <PrismComboSelectInput
      @buildLabelWithCount={{this.buildLabelWithCount}}
      @canUnselectAll={{false}}
      @isSmall={{@isSmall}}
      @onChange={{this.onChange}}
      @options={{this.options}}
      @selectAllLabel={{t
        "market.smart_insights.filter.priority.all_priorities"
      }}
      @value={{this.selectedOptions}}
      data-test-id="dropdown-priority-filter"
    />
  </template>
}
